<template>
  <div class="register-container">
    <!-- 背景装饰 -->
    <div class="background-overlay"></div>
    <div class="register-wrapper">
      <el-card class="register-card">
        <!-- 登录链接 -->
        <div class="login-link">
          <span>已有账号？</span>
          <router-link :to="{name:'login'}" class="link">立即登录</router-link>
        </div>
        
        <!-- 注册标题 -->
        <div class="register-header">
          <h2 class="title">创建新账户</h2>
          <p class="subtitle">加入我们的平台</p>
        </div>
        
        <!-- 注册表单 -->
        <el-form
          label-width="0px"
          :model="params"
          ref="registerForm"
          :rules="rules"
          class="register-form"
        >
          <el-form-item prop="account" class="form-item">
            <el-input 
              placeholder="用户名" 
              v-model="params.account"
              class="form-input"
            >
              <template #prefix>
                <el-icon><User /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item prop="department" class="form-item">
            <el-input 
              placeholder="部门" 
              v-model="params.department"
              class="form-input"
            >
              <template #prefix>
                <el-icon><OfficeBuilding /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item prop="password" class="form-item">
            <el-input
              placeholder="密码"
              v-model="params.password"
              type="password"
              class="form-input"
              show-password
            >
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item prop="confirmPassword" class="form-item">
            <el-input
              placeholder="确认密码"
              v-model="params.confirmPassword"
              type="password"
              class="form-input"
              show-password
            >
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item class="form-item">
            <el-button 
              type="primary" 
              @click="register"
              :loading="loading"
              class="register-button"
              :class="{ 'button-loading': loading }"
            >
              {{ loading ? '注册中...' : '注册' }}
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script>
import { userRegister } from "../api/user";
import { deepCopy } from "../utils/util";
import { ElMessage, ElIcon } from "element-plus";
import { User, Lock, OfficeBuilding } from "@element-plus/icons-vue";

const initParams = {
  account: "",
  department: "",
  password: "",
  confirmPassword: "",
};

export default {
  name: "Register",
  components: {
    ElIcon,
    User,
    Lock,
    OfficeBuilding
  },
  data() {
    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请输入密码"));
      } else if (value.length < 6) {
        callback(new Error("密码长度不能小于6位"));
      } else {
        callback();
      }
    };
    
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请确认密码"));
      } else if (value !== this.params.password) {
        callback(new Error("两次输入的密码不一致"));
      } else {
        callback();
      }
    };
    
    return {
      loading: false,
      params: deepCopy(initParams),
      rules: {
        account: [
          { required: true, message: "请输入用户名", trigger: "blur" },
          { min: 2, max: 20, message: "用户名长度应在2-20个字符之间", trigger: "blur" }
        ],
        department: [
          { required: true, message: "请输入部门", trigger: "blur" }
        ],
        password: [
          { required: true, message: "请输入密码", trigger: "blur" },
          { validator: validatePassword, trigger: ["blur", "change"] }
        ],
        confirmPassword: [
          { required: true, message: "请确认密码", trigger: "blur" },
          { validator: validateConfirmPassword, trigger: ["blur", "change"] }
        ]
      }
    };
  },
  mounted() {
    // 添加页面加载动画
    setTimeout(() => {
      this.$el.classList.add('loaded');
    }, 100);
  },
  methods: {
    register() {
      this.$refs.registerForm.validate((valid) => {
        if (valid) {
          this.loading = true;
          
          // 模拟表单提交动画
          const form = this.$el.querySelector('.register-form');
          form.classList.add('submitting');
          
          userRegister(this.params)
            .then((resp) => {
              this.loading = false;
              form.classList.remove('submitting');
              
              if (resp.status === 201) {
                ElMessage.error("该用户已存在，请重新输入账号！");
              } else {
                // 成功动画
                form.classList.add('success');
                this.params = deepCopy(initParams);
                ElMessage.success("注册成功，请联系管理员审核，审核成功后才能登录！");
                
                // 3秒后跳转登录页
                setTimeout(() => {
                  this.$router.push({ name: 'login' });
                }, 3000);
              }
            })
            .catch((err) => {
              console.error('注册失败:', err);
              this.loading = false;
              form.classList.remove('submitting');
              ElMessage.error("注册失败，请稍后重试");
            });
        } else {
          // 表单验证失败动画
          const form = this.$el.querySelector('.register-form');
          form.classList.add('error');
          setTimeout(() => {
            form.classList.remove('error');
          }, 500);
          return false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
// 变量定义
$primary-color: #409eff;
$success-color: #67c23a;
$danger-color: #f56c6c;
$transition: all 0.3s ease;
$border-radius: 12px;
$box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
$card-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
$input-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

.register-container {
  position: relative;
  height: 100vh;
  
  &.loaded {
    .register-wrapper {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  // 背景渐变叠加
  .background-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(64, 158, 255, 0.3) 0%, rgba(103, 194, 58, 0.3) 100%);
    backdrop-filter: blur(4px);
  }
  
  .register-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transform: translateY(20px);
    transition: $transition;
    transition-delay: 0.2s;
    
    .register-card {
      position: relative;
      width: 420px;
      padding: 40px;
      background: rgba(255, 255, 255, 0.95);
      border-radius: $border-radius;
      box-shadow: $card-shadow;
      backdrop-filter: blur(10px);
      transition: $transition;
      border: 1px solid rgba(255, 255, 255, 0.2);
      overflow: hidden;
      
      &::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
        transform: rotate(45deg);
        animation: cardShine 6s infinite;
      }
      
      .login-link {
        text-align: right;
        margin-bottom: 24px;
        font-size: 14px;
        color: #606266;
        
        .link {
          color: $primary-color;
          text-decoration: none;
          font-weight: 500;
          transition: $transition;
          
          &:hover {
            color: #66b1ff;
            text-decoration: underline;
          }
        }
      }
      
      .register-header {
        text-align: center;
        margin-bottom: 32px;
        
        .title {
          font-size: 28px;
          font-weight: 600;
          color: #303133;
          margin-bottom: 8px;
          line-height: 1.2;
        }
        
        .subtitle {
          font-size: 14px;
          color: #909399;
          margin: 0;
        }
      }
      
      .register-form {
        transition: $transition;
        
        &.submitting {
          opacity: 0.8;
        }
        
        &.success {
          animation: formSuccess 0.5s ease;
        }
        
        &.error {
          animation: formError 0.5s ease;
        }
        
        .form-item {
          margin-bottom: 24px;
          position: relative;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .form-input {
            width: 100%;
            height: 44px;
            border-radius: 8px;
            border: 1px solid #dcdfe6;
            transition: $transition;
            background-color: rgba(255, 255, 255, 0.8);
            
            &:focus {
              border-color: $primary-color;
              box-shadow: $input-shadow, 0 0 0 2px rgba(64, 158, 255, 0.1);
            }
            
            &.is-error {
              border-color: $danger-color;
              box-shadow: 0 0 0 2px rgba(245, 108, 108, 0.1);
            }
          }
          
          .register-button {
            width: 100%;
            height: 44px;
            font-size: 16px;
            font-weight: 500;
            border-radius: 8px;
            border: none;
            background-color: $primary-color;
            color: white;
            transition: $transition;
            position: relative;
            overflow: hidden;
            
            &:hover:not(:disabled) {
              background-color: #66b1ff;
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
            }
            
            &:active:not(:disabled) {
              transform: translateY(0);
            }
            
            &.button-loading {
              background-color: #a0cfff;
            }
          }
        }
      }
    }
  }
}

// 动画定义
@keyframes backgroundScale {
  0% {
    background-size: 110% 110%;
  }
  100% {
    background-size: 100% 100%;
  }
}

@keyframes cardShine {
  0% {
    left: -50%;
    top: -50%;
  }
  100% {
    left: 150%;
    top: 150%;
  }
}

@keyframes formSuccess {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 30px rgba(103, 194, 58, 0.4);
  }
}

@keyframes formError {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

// Element Plus 样式覆盖
:deep(.el-input__inner) {
  border-radius: 8px;
  height: 44px;
  padding: 0 15px;
  font-size: 14px;
}

:deep(.el-input__prefix-inner) {
  left: 12px;
}

:deep(.el-input__icon) {
  color: #c0c4cc;
}

:deep(.el-button--primary) {
  background-color: $primary-color;
  border-color: $primary-color;
}

:deep(.el-button--primary:hover) {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

:deep(.el-button--primary.is-loading) {
  background-color: #a0cfff;
  border-color: #a0cfff;
}

:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}
</style>